<template lang="pug">
BriqPanel.custom-fields-panel(:data-test-id="panelDataTestId")
  h2 {{ title }}
  .columns
    .column
      div(v-for="(customField, index) in customFieldDefinitions" :key="index")
        component(
          :is="getComponent(customField)"
          :customField="customField"
          :error="error(customField, index)"
          :value="getCustomField(customField)"
          @input="v => setCustomField(customField.FieldCode, v)"
          )
</template>

<script>
import { propEq, filter, includes, map, pipe, path, prop, ifElse, find, propOr } from 'ramda'
import CustomFieldCheckbox from './CustomFieldsPanel/CustomFieldCheckbox.vue'
import CustomFieldInput from './CustomFieldsPanel/CustomFieldInput.vue'
import CustomFieldDropdown from './CustomFieldsPanel/CustomFieldDropdown.vue'
import CustomFieldDuration from './CustomFieldsPanel/CustomFieldDuration.vue'
import buildCustomFieldErrorMessage from './buildCustomFieldErrorMessage.js'
import toKebab from '@/helpers/toKebab.js'

export default {
  name: 'CustomFieldsPanel',
  components: {
    CustomFieldCheckbox,
    CustomFieldInput,
    CustomFieldDuration,
    CustomFieldDropdown,
  },
  props: {
    title: { type: String, required: true },
    customFields: { type: Array, required: true },
    customFieldDefinitions: Array,
    customFieldsInVuelidate: Object,
  },
  computed: {
    panelDataTestId() {
      return `custom-field-panel-for-${toKebab(this.title)}`
    },
  },
  methods: {
    getComponent(customField) {
      if (
        customField.Datatype === 'String' ||
        customField.Datatype === 'Integer' ||
        customField.Datatype === 'Decimal'
      )
        return 'CustomFieldInput'
      if (customField.Datatype === 'Boolean') return 'CustomFieldCheckbox'
      if (customField.Datatype === 'Time') return 'CustomFieldDuration'
      if (customField.Datatype === 'SingleList' || customField.Datatype === 'MultiList')
        return 'CustomFieldDropdown'
    },
    getCustomField({ Datatype, FieldCode }) {
      const findField = find(propEq('FieldCode', FieldCode))
      const field = findField(this.customFields)
      const value = path(['Values', 0])(field)
      if (Datatype === 'Boolean') {
        return (typeof value === 'boolean' && value) || value === 'true'
      }
      if (Datatype === 'MultiList') {
        // multilist dropdown needs an array of objects with Name and Value property in it.
        const fieldValues = field?.Values
        const filterOptions = fieldValues ? filter(o => includes(o.Value, fieldValues)) : x => x
        return pipe(
          findField,
          propOr([], 'ValueOptions'),
          filterOptions,
        )(this.customFieldDefinitions || [])
      }
      return value
    },
    setCustomField(fieldCode, value) {
      const existing = this.customFields.find(propEq('FieldCode', fieldCode))
      // Multilist returns array of objects with Value property in it so we need to extract only values
      const fieldValues = ifElse(Array.isArray, map(prop('Value')), x => [x])(value)
      if (!existing) {
        this.customFields.push({
          FieldCode: fieldCode,
          Values: fieldValues,
        })
      } else {
        // a bit nasty hack to prevent rerendering loop
        if (Array.isArray(value) && existing.Values.length !== fieldValues.length)
          existing.Values = fieldValues
        else if (!Array.isArray(value)) existing.Values = fieldValues
      }
      // todo: touch validation
    },
    error(customField, index) {
      return buildCustomFieldErrorMessage({
        customFieldsInVuelidate: this.customFieldsInVuelidate,
        customField: customField,
        indexOfField: index,
      })
    },
  },
}
</script>

<style lang="stylus" scoped>
h2
  margin-bottom 20px
</style>

<style lang="stylus">
@import '~@v/variables.styl'

.custom-fields-panel .briq-form-element
  margin-bottom space-S
</style>
